import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ProjectStakeholder } from '../../services/commercialProjectsApi';
import { StakeholderQuickCreateModal } from './StakeholderQuickCreateModal';

type MeetingParticipantsFieldProps = {
  projectId: string;
  canWrite: boolean;
  stakeholders: ProjectStakeholder[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onStakeholderCreated: (stakeholder: ProjectStakeholder) => void;
  showTitle?: boolean;
};

export function MeetingParticipantsField({
  projectId,
  canWrite,
  stakeholders,
  selectedIds,
  onChange,
  onStakeholderCreated,
  showTitle = true,
}: MeetingParticipantsFieldProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const selected = stakeholders.filter((person) => selectedIds.includes(person.id));
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return stakeholders.filter((person) => {
      if (selectedIds.includes(person.id)) return false;
      if (!term) return true;
      return person.name.toLowerCase().includes(term)
        || (person.organization ?? '').toLowerCase().includes(term)
        || (person.role ?? '').toLowerCase().includes(term);
    });
  }, [query, selectedIds, stakeholders]);

  function add(person: ProjectStakeholder): void {
    onChange([...selectedIds, person.id]);
    setQuery('');
    setOpen(false);
  }

  function remove(id: string): void {
    onChange(selectedIds.filter((item) => item !== id));
  }

  return (
    <div className="erp-label md:col-span-4">
      {showTitle ? 'Participantes da reunião' : null}
      <p className="mb-1 text-xs font-normal text-secondary-500">
        A pessoa precisa constar em Stakeholders.
      </p>
      {selected.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-1">
          {selected.map((person) => (
            <li key={person.id} className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-1 text-xs text-secondary-800">
              <span>{person.name}</span>
              {canWrite ? (
                <button type="button" className="text-secondary-500 hover:text-secondary-800" onClick={() => remove(person.id)}>
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {canWrite ? (
        <div ref={rootRef} className="relative">
          <input
            className="erp-input"
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            value={query}
            placeholder="Busque pelo nome do participante"
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
          />
          {open ? (
            <ul
              id={listId}
              role="listbox"
              className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
            >
              <li>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-primary-700 hover:bg-primary-50"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    setCreateOpen(true);
                  }}
                >
                  <PlusIcon className="h-4 w-4" aria-hidden />
                  Criar stakeholder
                </button>
              </li>
              {visible.length === 0 ? (
                <li className="px-3 py-2 text-sm text-secondary-500">Nenhum stakeholder encontrado.</li>
              ) : (
                visible.map((person) => (
                  <li key={person.id}>
                    <button
                      type="button"
                      role="option"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary-50"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => add(person)}
                    >
                      <span className="font-medium text-secondary-900">{person.name}</span>
                      {person.role || person.organization ? (
                        <span className="ml-2 text-secondary-500">
                          {[person.role, person.organization].filter(Boolean).join(' · ')}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
      ) : null}
      <StakeholderQuickCreateModal
        open={createOpen}
        projectId={projectId}
        onClose={() => setCreateOpen(false)}
        onCreated={(stakeholder) => {
          onStakeholderCreated(stakeholder);
          onChange([...selectedIds, stakeholder.id]);
        }}
      />
    </div>
  );
}
